import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { get, put } from "@/lib/api";
import { PageLoader, PageHeader, EmptyState, StatCard } from "@/components/common";
import { Gift, Users, Wallet, Save } from "lucide-react";

export default function AdminReferrals() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-referrals"], queryFn: () => get("/admin/referrals") });
  const [amount, setAmount] = useState(50);
  useEffect(() => { if (data?.reward_amount != null) setAmount(data.reward_amount); }, [data]);

  const saveReward = async () => {
    await put("/admin/settings/referral-reward", { amount: Number(amount) });
    toast.success("Reward amount updated");
    qc.invalidateQueries({ queryKey: ["admin-referrals"] });
  };

  if (isLoading || !data) return <PageLoader />;

  return (
    <div>
      <PageHeader title="Referral & Reward Program" subtitle="Both the referrer and the new student earn wallet credit on signup" />

      <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
        <p className="font-display font-bold text-slate-900 mb-1 flex items-center gap-2"><Gift className="h-5 w-5 text-indigo-700" /> Reward per referral</p>
        <p className="text-sm text-slate-500 mb-3">Each side receives this amount in their wallet when a referred student signs up.</p>
        <div className="flex items-center gap-2">
          <span className="text-slate-500 font-semibold">₹</span>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="reward-amount-input" className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-32" />
          <button onClick={saveReward} data-testid="save-reward-btn" className="bg-indigo-800 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm"><Save className="h-4 w-4" /> Save</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <StatCard icon={Users} label="Total Referrals" value={data.count} tone="violet" />
        <StatCard icon={Gift} label="Reward / side" value={`₹${data.reward_amount}`} tone="jade" />
        <StatCard icon={Wallet} label="Total Rewarded" value={`₹${data.total_rewarded}`} tone="amber" />
      </div>

      {!data.referrals.length ? <EmptyState icon={Gift} title="No referrals yet" hint="Referrals appear here as students invite friends." /> : (
        <div className="bg-white rounded-2xl border border-slate-200 p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-referrals-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left p-3">Referrer</th><th className="text-left p-3">New Student</th><th className="text-left p-3">Code</th><th className="text-left p-3">Reward (each)</th><th className="text-left p-3">Date</th></tr>
            </thead>
            <tbody>
              {data.referrals.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="p-3"><p className="font-semibold text-slate-800">{r.referrer_name}</p><p className="text-xs text-slate-400">{r.referrer_email}</p></td>
                  <td className="p-3">{r.referred_name}</td>
                  <td className="p-3 font-mono text-xs">{r.code}</td>
                  <td className="p-3 font-semibold text-teal-700">₹{r.reward_amount || 0}</td>
                  <td className="p-3 text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
